// frontend/src/components/asset/ChangeAccidentDriverModal.js
/**
 * Custodian override of the named driver on a vehicle accident.
 * Theme: Admin (red).
 */

import React, { useState } from 'react';
import { UserCheck } from 'lucide-react';
import Modal from '../UI/Modal';
import SearchableCombobox from '../UI/SearchableCombobox';
import { useStaffList } from '../../hooks/useStaffData';
import { useDebounce } from '../../hooks/useDebounce';
import { useChangeAccidentDriver } from '../../hooks/useAssetInventoryData';

const ChangeAccidentDriverModal = ({ isOpen, onClose, accident }) => {
  const [staffId, setStaffId] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const { data: staffData } = useStaffList({
    search: debouncedSearch.trim().length >= 2 ? debouncedSearch : undefined,
    page_size: 10,
  });
  const staffList = staffData?.results || [];
  const changeDriver = useChangeAccidentDriver();

  const handleClose = () => {
    setStaffId('');
    setSearch('');
    onClose();
  };

  const handleSubmit = () => {
    if (!staffId || !accident?.id) return;
    changeDriver.mutate(
      { id: accident.id, driver_staff_id: staffId },
      { onSuccess: handleClose },
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Change Accident Driver"
      size="md"
      theme="admin"
      contentClassName="space-y-4"
      footer={
        <>
          <button type="button" onClick={handleClose} className="btn btn-admin-outline">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!staffId || changeDriver.isPending}
            className="btn btn-admin-primary"
          >
            <UserCheck className="w-4 h-4 mr-2" />
            {changeDriver.isPending ? 'Saving...' : 'Update Driver'}
          </button>
        </>
      }
    >
      <p className="text-sm text-gray-600">
        Accident <span className="font-medium text-gray-900">{accident?.reference}</span> for{' '}
        {accident?.vehicle_number_raw}.
      </p>
      {accident?.driver_display_name && (
        <p className="text-xs text-gray-500">
          Currently named: {accident.driver_display_name}
          {accident.driver_staff_id && ` (${accident.driver_staff_id})`}
        </p>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Driver <span className="text-red-600">*</span>
        </label>
        <SearchableCombobox
          items={staffList}
          value={staffId}
          onChange={setStaffId}
          onSearch={setSearch}
          displayFn={(s) => `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.staff_id}
          valueFn={(s) => s.staff_id}
          searchFn={() => true}
          placeholder="Search staff..."
          searchPlaceholder="Type name or staff ID..."
          variant="form"
          theme="admin"
          className="w-full"
        />
      </div>
    </Modal>
  );
};

export default ChangeAccidentDriverModal;
